import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, List, ExternalLink, FileText, Hammer, Image as ImageIcon } from "lucide-react";
import api, { API } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Badge } from "./panels/common";

/* Contextual workspace opened by a REAL Luna open_record action. Loads the actual record; reports
   loading → ready | error to the scene (drives the HUD sweep). Nothing is invented: not found → short message. */
const EASE = [0.22, 1, 0.36, 1];
const statusColors = { draft: "gray", sent: "amber", partial: "blue", accepted: "green", started: "purple",
  planning: "blue", active: "green", on_hold: "amber", completed: "gray", cancelled: "red" };

export const RecordSpotlight = ({ kind, id, label, onClose, onShowList, onStatus }) => {
  const { tx } = useI18n();
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    let alive = true, url = null;
    (async () => {
      try {
        if (kind === "proposals") {
          const [list, pdf] = await Promise.all([api.get("/proposals"), api.get(`/proposals/${id}/pdf`, { responseType: "blob" })]);
          const doc = list.data.find((p) => p.proposal_id === id);
          if (!doc) throw Object.assign(new Error(tx("Bu teklif artık kayıtlarda yok.")), { soft: true });
          url = URL.createObjectURL(pdf.data);
          alive && setState({ status: "ready", doc, url });
        } else if (kind === "projects") {
          const [list, clients] = await Promise.all([api.get("/projects"), api.get("/clients")]);
          const doc = list.data.find((p) => p.project_id === id);
          if (!doc) throw Object.assign(new Error(tx("Bu proje artık kayıtlarda yok.")), { soft: true });
          alive && setState({ status: "ready", doc, client: clients.data.find((c) => c.client_id === doc.client_id) });
        } else if (kind === "files") {
          const [list, data] = await Promise.all([api.get("/files"), api.get(`/files/${id}/data`)]);
          const doc = list.data.find((f) => f.file_id === id);
          if (!doc) throw Object.assign(new Error(tx("Bu dosya artık kayıtlarda yok.")), { soft: true });
          alive && setState({ status: "ready", doc, data: data.data.data, contentType: data.data.content_type });
        }
      } catch (e) {
        const code = e?.response?.status;
        const msg = e.soft ? e.message : code === 404 ? tx("Kayıt bulunamadı.") : code ? tx("Yüklenemedi (HTTP {code}).", { code }) : tx("Yüklenemedi — bağlantı hatası.");
        alive && setState({ status: "error", message: msg });
      }
    })();
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [kind, id]);

  useEffect(() => { if (state.status !== "loading") onStatus?.(state.status); }, [state.status, onStatus]);

  const title = kind === "proposals" ? tx("Teklif") : kind === "projects" ? tx("Proje") : tx("Dosya");
  const Icon = kind === "proposals" ? FileText : kind === "projects" ? Hammer : ImageIcon;

  return (
    <motion.section data-testid="record-spotlight" data-kind={kind} data-status={state.status} role="region" aria-label={tx("{title} önizleme", { title })}
      className="h-full flex flex-col px-3 sm:px-6 pb-4 pt-1 max-w-6xl mx-auto w-full"
      initial={{ opacity: 0, y: 18, scale: 0.985 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.99 }}
      transition={{ duration: 0.42, ease: EASE }}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Icon size={16} className="text-cyan-300 shrink-0" />
          <span className="label-eyebrow shrink-0">{title}</span>
          <span className="text-sm text-white/85 truncate" data-testid="spotlight-title">{state.doc ? headline(kind, state.doc) : label}</span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={onShowList} data-testid="spotlight-list-btn" className="h-9 px-3 rounded-full border border-white/10 text-xs text-white/65 hover:text-cyan-200 hover:border-cyan-400/40 inline-flex items-center gap-1.5"><List size={14} /> {tx("Liste")}</button>
          <button onClick={onClose} data-testid="spotlight-close-btn" aria-label={tx("Kapat")} className="h-9 w-9 rounded-full border border-white/10 text-white/65 hover:text-white hover:border-cyan-400/40 inline-flex items-center justify-center"><X size={15} /></button>
        </div>
      </div>

      <div className="panel corner-bracket flex-1 min-h-[320px] overflow-hidden relative">
        {state.status === "loading" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/55 text-sm" data-testid="spotlight-loading">
            <div className="hud-loading-line" /> {tx("Yükleniyor…")}
          </div>
        )}
        {state.status === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6" data-testid="spotlight-error">
            <p className="text-sm text-white/80">{state.message}</p>
            <button onClick={onClose} data-testid="spotlight-error-close-btn" className="px-4 py-2 rounded-full border border-cyan-400/40 text-cyan-100 text-sm hover:bg-cyan-500/15">{tx("Ana sahneye dön")}</button>
          </div>
        )}
        {state.status === "ready" && kind === "proposals" && <ProposalView s={state} id={id} />}
        {state.status === "ready" && kind === "projects" && <ProjectView s={state} />}
        {state.status === "ready" && kind === "files" && <FileView s={state} />}
      </div>
    </motion.section>
  );
};

const headline = (kind, d) => kind === "proposals" ? `${d.number} · ${d.client?.name || d.title || "—"} · £${Number(d.total || 0).toFixed(2)}`
  : kind === "projects" ? `${d.name}${d.address ? ` · ${d.address}` : ""}` : (d.filename || d.name);

const ProposalView = ({ s, id }) => {
  const { tx } = useI18n();
  return (
  <div className="h-full flex flex-col" data-testid="spotlight-proposal">
    <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 text-xs">
      <div className="flex items-center gap-2 text-white/70">
        <Badge color={statusColors[s.doc.status] || "gray"}>{s.doc.status}</Badge>
        <span>{s.doc.vat_mode === "none" ? "No VAT" : `VAT ${s.doc.vat_rate_percent}%`}</span>
        {s.doc.title && <span className="text-white/50 truncate max-w-[40ch]">{s.doc.title}</span>}
      </div>
      <a href={`${API}/proposals/${id}/pdf`} target="_blank" rel="noreferrer" data-testid="spotlight-pdf-newtab" className="inline-flex items-center gap-1 text-cyan-300 hover:text-cyan-200"><ExternalLink size={13} /> {tx("Yeni sekmede")}</a>
    </div>
    <object title={tx("Teklif PDF")} data={`${s.url}#toolbar=0&navpanes=0`} type="application/pdf" className="flex-1 w-full bg-[#0b1020]" data-testid="spotlight-pdf-frame">
      <div className="h-full flex flex-col items-center justify-center gap-2 text-sm text-white/65 p-6 text-center">
        {tx("Bu tarayıcıda gömülü PDF görüntüleyici yok.")}
        <a href={s.url} target="_blank" rel="noreferrer" className="text-cyan-300 underline">{tx("PDF'i yeni sekmede aç")}</a>
      </div>
    </object>
  </div>
  );
};

const ProjectView = ({ s }) => {
  const { tx } = useI18n();
  const d = s.doc;
  return (
    <div className="p-5 sm:p-7 grid sm:grid-cols-[1.3fr_1fr] gap-6" data-testid="spotlight-project">
      <div>
        <h2 className="text-2xl font-semibold text-white tracking-tight">{d.name}</h2>
        {d.address && <p className="text-sm text-white/60 mt-1">{d.address}</p>}
        {d.scope && <p className="text-sm text-white/80 mt-4 whitespace-pre-wrap leading-relaxed">{d.scope}</p>}
        {d.notes && <p className="text-xs text-white/55 mt-3 whitespace-pre-wrap">{d.notes}</p>}
      </div>
      <div className="space-y-3 text-sm">
        <Row l={tx("Durum")} r={<Badge color={statusColors[d.status] || "cyan"}>{d.status}</Badge>} />
        <Row l={tx("Müşteri")} r={s.client?.name || "—"} />
        <Row l={tx("Bitiş")} r={d.due_date || "—"} />
        <div>
          <div className="flex justify-between text-white/60 text-xs mb-1"><span>{tx("İlerleme")}</span><span className="font-mono text-cyan-200">{d.progress || 0}%</span></div>
          <div className="h-1.5 bg-black/40 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500" style={{ width: `${d.progress || 0}%` }} /></div>
        </div>
      </div>
    </div>
  );
};

const FileView = ({ s }) => {
  const { tx } = useI18n();
  return (
  <div className="h-full flex flex-col" data-testid="spotlight-file">
    <div className="px-4 py-2 border-b border-white/10 text-xs text-white/65 flex items-center justify-between">
      <span className="truncate">{s.doc.filename}{s.doc.caption ? ` · ${s.doc.caption}` : ""}</span>
      {s.doc.project_name && <span className="text-white/45">{s.doc.project_name}</span>}
    </div>
    {(s.contentType || "").startsWith("image/")
      ? <div className="flex-1 flex items-center justify-center p-4 bg-black/30"><img src={s.data} alt={s.doc.caption || s.doc.filename} className="max-h-full max-w-full rounded-lg border border-cyan-500/20" /></div>
      : (s.contentType || "").includes("pdf")
        ? <iframe title={s.doc.filename} src={s.data} className="flex-1 w-full bg-[#0b1020]" />
        : <div className="flex-1 flex items-center justify-center text-sm text-white/60">{tx("Bu dosya türü için önizleme yok ({ct}).", { ct: s.contentType || tx("bilinmiyor") })}</div>}
  </div>
  );
};

const Row = ({ l, r }) => <div className="flex items-center justify-between border-b border-white/5 pb-2"><span className="text-white/55">{l}</span><span className="text-white/90">{r}</span></div>;
